import type { ExpenseSettlementMethod } from '../../expenses/domain/expenseTypes';
import type { BranchTreasuryPeriodSummary } from './reportsTreasuryTypes';

const SETTLEMENT_METHODS = new Set<ExpenseSettlementMethod>([
  'cash_drawer',
  'transfer',
  'card',
  'check',
  'other',
]);

function parseMethodRow(raw: unknown): { methodId: string; total: number } | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const methodId = String(row.method_id ?? '');
  if (!methodId) return null;
  const total = Number(row.total ?? 0);
  return { methodId, total: Number.isFinite(total) ? total : 0 };
}

function parseCategoryRow(raw: unknown): { category: string; total: number } | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const category = String(row.category ?? '');
  if (!category) return null;
  const total = Number(row.total ?? 0);
  return { category, total: Number.isFinite(total) ? total : 0 };
}

function parseSettlementRow(raw: unknown): { method: ExpenseSettlementMethod; total: number } | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const method = String(row.method ?? '') as ExpenseSettlementMethod;
  if (!SETTLEMENT_METHODS.has(method)) return null;
  const total = Number(row.total ?? 0);
  return { method, total: Number.isFinite(total) ? total : 0 };
}

export function parseTreasuryPeriodSummary(payload: Record<string, unknown>): BranchTreasuryPeriodSummary | null {
  const collectionsByMethod = Array.isArray(payload.collections_by_method)
    ? payload.collections_by_method.map(parseMethodRow).filter((r): r is NonNullable<typeof r> => r != null)
    : [];

  const outflowsByCashCategory = Array.isArray(payload.outflows_by_cash_category)
    ? payload.outflows_by_cash_category.map(parseCategoryRow).filter((r): r is NonNullable<typeof r> => r != null)
    : [];

  const expenseSettlementsByMethod = Array.isArray(payload.expense_settlements_by_method)
    ? payload.expense_settlements_by_method
        .map(parseSettlementRow)
        .filter((r): r is NonNullable<typeof r> => r != null)
    : [];

  const notes = payload.notes;
  const doubleCountPolicyNote =
    notes && typeof notes === 'object' && typeof (notes as Record<string, unknown>).double_count_policy === 'string'
      ? String((notes as Record<string, unknown>).double_count_policy)
      : null;

  return {
    salesCollectionsTotal: Number(payload.sales_collections_total ?? 0),
    customerPaymentsTotal: Number(payload.customer_payments_total ?? 0),
    cashInflowsTotal: Number(payload.cash_inflows_total ?? 0),
    collectionsTotal: Number(payload.collections_total ?? 0),
    collectionsByMethod,
    expenseSettlementsTotal: Number(payload.expense_settlements_total ?? 0),
    standaloneCashOutflowsTotal: Number(payload.standalone_cash_outflows_total ?? 0),
    outflowsTotal: Number(payload.outflows_total ?? 0),
    ownerWithdrawalsTotal: Number(payload.owner_withdrawals_total ?? 0),
    outflowsByCashCategory,
    expenseSettlementsByMethod,
    treasuryBalance: Number(payload.treasury_balance ?? 0),
    doubleCountPolicyNote,
  };
}
